<script lang="ts" setup>
import { ref, computed, watch, onMounted } from 'vue'
import { storeToRefs } from 'pinia'

import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import RoomieButton from '@lahaus-roomie/roomie/src/components/RoomieButton/index.vue'
import OnboardingHeader from './Header/index.vue'
import OnboardingHero from './Hero/index.vue'
import OnboardingEmptyResultsBanner from './EmptyResultsBanner/index.vue'
import OnboardingStepsModal from './StepsModal/index.vue'
import OnboardingFiltersBar from './FiltersBar/index.vue'
import OnboardingResultsCarousel from './ResultsCarousels/index.vue'
import OnboardingChatOverlay from './ChatOverlay/index.vue'
import AppAiChatWindow from '@/components/App/Ai/Chat/Window/index.vue'
import LockOverlay from '@/components/LockOverlay/index.vue'

import './_index.scss'
import { deepCopy } from '@/utils/objects'
import COUNTRIES_DICT from '@/constants/locations/countries.json'
import type { OnboardingFilters } from '@/components/OnboardingContent/types'
import { SCREEN, SCREEN_CTA } from '@/utils/segment'

import { useUserStore } from '@/stores/userStore'
import { useLocationsStore } from '@/stores/locationsStore'
import { useSearchFiltersStore } from '@/stores/searchFiltersStore'
import OnboardingOfflineAgent from '@/components/OnboardingContent/utils/OnboardingOfflineAgent/index.js'

const route = useRoute()
const router = useRouter()
const device = useDevice()
const { t, locale } = useI18n()
const { $getProjectRecomGroups, $sobreplanosDataService, $trackPage, $trackEvent } = useNuxtApp()

const DEFAULT_CLUSTER_COUNTRY = {
  co: 'rodadero',
  mx: 'roma-condesa'
}

const DEFAULT_CLUSTER = DEFAULT_CLUSTER_COUNTRY[locale.value || 'mx']

const locationsStore = useLocationsStore()
const userStore = useUserStore()

await callOnce(async () => {
  if (locationsStore.areLocationsLoaded) return

  await locationsStore.initializeLocations()
})

const {
  getFiltersClassified,
  getResults
} = useOnboarding()

const isSamOnboardingEnabled = computed(() => false)

const isLoading = ref(false)
const results = ref<Record<string, any> | null>(null)
const outstandingProject = ref()
const isReadyToFetch = ref(false)
const breakpoints = useBreakpoints(breakpointsTailwind)
const isDesktop = breakpoints.greater('md')
const desktopSamLayout = ref(!!isDesktop.value && !!isSamOnboardingEnabled.value)
const searchFiltersStore = useSearchFiltersStore()
const { currentFilters } = storeToRefs(searchFiltersStore)

const currentBusinessHubsCode = computed(() => {
  const businessHubs = locationsStore.locationsQueryUtils.getBusinessHubsByNeighborhoodsValues(currentFilters.value.neighborhoods)

  return businessHubs.map(({ code }) => code)[0]
})

useSeoMeta({
  robots: 'index, follow',
  title: t('metadata.title', { location: COUNTRIES_DICT[locale.value] }),
  ogTitle: t('metadata.title'),
  ogImage: 'https://lahaus.imgix.net/static/default/default-share-social-media-v2.jpg',
  ogLocale: t('investment_simulator.seo_metadata.locale'),
  ogSiteName: 'La Haus',
  twitterCard: 'summary',
  twitterSite: `@${t('investment_simulator.seo_metadata.twitterSite')}`,
  twitterCreator: `@${t('investment_simulator.seo_metadata.twitterSite')}`
})

const fetchResults = async () => {
  isLoading.value = true

  const filters = getFiltersClassified()
  results.value = await getResults(filters)
  outstandingProject.value = results.value?.outstandingProject

  isLoading.value = false
}

const handleGoBack = async () => {
  if (route.query.step) {
    const lastStepIndex = parseInt(route.query.step as string)

    await router.replace({
      query: {
        ...route.query,
        step: (lastStepIndex - 1).toString()
      }
    })
  }

  $trackPage()
  results.value = null
}

const goToInvestmentSimulator = () => {
  navigateTo({
    path: '/venta/propiedades',
    query: {
      neighborhoods: currentFilters.value.neighborhoods.join('~') || undefined
    }
  })
}

const goToHome = () => {
  navigateTo({ path: '/' })
}

const areResultsVisible = computed(() => !!results.value)

const onboardingAgent = new OnboardingOfflineAgent({
  t,
  device,
  $sobreplanosDataService,
  $trackEvent,
  initialFilters: deepCopy(currentFilters.value),
  customTitle: route.query.custom_title
}, locale.value)

const heroPictures = computed(() => {
  if (!isHeroVisible.value) return []

  const coverPicture = outstandingProject.value?.pictures_v2.find((picture: Record<string, string>) => picture.type === 'cover')

  const otherPictures = outstandingProject.value?.pictures_v2.filter((picture: Record<string, string>) => picture.type === 'gallery')
  const selectedPictures = [coverPicture, ...otherPictures].slice(0, 5)
  return selectedPictures.map((picture: Record<string, string>) => `${picture?.url}?auto=format,compress,enhance`)
})

const isHeroVisible = computed(() => !!outstandingProject.value?.code)

const exactMatchResultsIds = computed(() => [outstandingProject.value?.code, ...results.value.exactMatchProjects.map(project => project.code)].filter(Boolean))

const isContentLocked = computed(() => !userStore.isAuthenticated)

const handleOnboardingFinish = (filters: OnboardingFilters) => {
  isReadyToFetch.value = true
  currentFilters.value = filters
}

const isTopPage = ref(true)

const checkScroll = () => {
  const scrollTop = document.documentElement.scrollTop

  isTopPage.value = scrollTop === 0
}

watch(currentFilters, async () => {
  if (!isReadyToFetch.value) return

  await fetchResults()
}, { deep: true })

onMounted(async () => {
  const routeClusters = route.query.clusters as string
  let mainCluster

  if (routeClusters) {
    mainCluster = routeClusters.split('~')
  } else {
    mainCluster = DEFAULT_CLUSTER
  }

  const recomendationsGroups = await $getProjectRecomGroups(mainCluster)

  window.addEventListener('scroll', () => {
    checkScroll()
  }, { passive: true })
})
</script>

<script lang="ts">
export default {
  name: 'OnboardingContent'
}
</script>

<template>
  <section
    class="onboarding-v2 onboarding-contnent">
    <div
      v-if="areResultsVisible"
      class="onboarding-header-container relative bg-white"
      :class="[desktopSamLayout ? 'mt-80' : '']">
      <ClientOnly>
        <OnboardingHeader
          :is-sticky="desktopSamLayout"
          @go-back="handleGoBack"
          @go-home="goToHome" />
      </ClientOnly>
    </div>

    <div
      v-if="areResultsVisible"
      :class="{ 'wrapped-layout md:ml-82': desktopSamLayout }">
      <div
        class="onboarding-v2__results-page transition-all duration-300 w-full"
        :class="{ 'wrapped-layout': desktopSamLayout }">
        <div class="sticky left-0 top-0 bg-white z-50 px-0 md:px-36">
          <OnboardingFiltersBar
            class="py-10 my-12 pr-10 max-w-[180rem] mx-auto"
            :class="{ 'top-4': desktopSamLayout }" />
        </div>

        <OnboardingHero
          v-if="isHeroVisible"
          class="onboarding-v2__hero"
          :class="{ 'blur-sm': isLoading, 'mb-40': !isContentLocked, 'md:mb-24': isContentLocked }"
          :name="outstandingProject!.name"
          :city="outstandingProject!.city_label"
          :neighborhood="outstandingProject!.neighborhood_label"
          :min-price="outstandingProject!.price"
          :typologies="outstandingProject!.typologies"
          :delivery-date="outstandingProject!.delivered_at"
          :cover-urls="heroPictures"
          :path="outstandingProject!.path" />

        <OnboardingEmptyResultsBanner
          v-if="!outstandingProject"
          class="onboarding-empty-results-banner" />

        <div
          class="relative"
          :class="{ 'mb-0 md:mb-72': isContentLocked }">
          <LockOverlay
            v-if="isContentLocked"
            :screen="SCREEN.INVESTMENT_QUIZ"
            :listing-ids="exactMatchResultsIds.slice(0,1)"
            :hub-code="currentBusinessHubsCode"
            show-general-title
            class="z-40 absolute h-full" />

          <OnboardingResultsCarousel
            :class="{ 'blur-sm': isLoading, 'mb-40': !isContentLocked, 'pt-8 mb-0': isContentLocked }"
            :exact-match-projects="results?.exactMatchProjects"
            :extended-search="results?.extendedSearch"
            :extra-results="results?.extraResults"
            :carousels-limit="isContentLocked ? 1 : null"
            :is-loading />
        </div>

        <div
          v-if="!isContentLocked"
          class="w-full max-w-[180rem] mx-auto px-24 lg:px-40 mt-24 lg:mt-54"
          :class="isSamOnboardingEnabled ? 'mb-100 md:mb-54' : 'mb-54'">
          <RoomieButton
            id="onboarding-button-to-investment-simulator"
            data-lh-id="onboarding-button-to-investment-simulator"
            :aria-label="t('allProjects')"
            type="button"
            variant="outlined"
            @click="goToInvestmentSimulator">
            {{ t('allProjects') }}
          </RoomieButton>
        </div>

        <AppAiChatWindow
          v-if="isSamOnboardingEnabled"
          :offline-agent="onboardingAgent"
          :screen="SCREEN.INVESTMENT_QUIZ + '_SAM_ONBOARDING'"
          :screen-cta="SCREEN_CTA.INVESTMENT_WEB_CHAT"
          open-after-init />
      </div>
    </div>

    <div
      v-else-if="isSamOnboardingEnabled"
      class="w-screen h-screen onboarding-modal__bg">
      <OnboardingChatOverlay
        :offline-agent="onboardingAgent"
        @finish="handleOnboardingFinish" />
    </div>

    <OnboardingStepsModal
      v-else
      :loading="isLoading"
      :initial-filters="currentFilters"
      @finish="handleOnboardingFinish" />
  </section>
</template>

<i18n src="./i18n.json" lang="json" />
